import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeam } from '@/providers/TeamProvider';
import { useEffect, useState } from 'react';
import { Role, TeamMember } from '@/hooks/api/models';
import { useDestroyTeamMember } from '@/hooks/api/0320_destroy_team_member';
import { useTeamInvalidate } from '@/hooks/api/0210_team';
import { toastSuccess } from '@/lib/toast';
import { Link } from 'react-router-dom';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';

export const TeamMembersElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, me, _t_, isAdmin } = useCurrentTeam();
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember>();
  const { mutate, isPending, isSuccess, error } = useDestroyTeamMember();
  const { invalidate: invalidateTeam } = useTeamInvalidate({ identifier: _w_!.identifier, id: _t_!.id });

  const deleteConfirmOk = () => {
    mutate({ identifier: _w_!.identifier, team_id: _t_!.id, team_member_id: selectedTeamMember!.id });
  };

  useEffect(() => {
    if (isSuccess) {
      toastSuccess('チームメンバーを削除しました');
      invalidateTeam();
    }
  }, [isSuccess]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center">
        <div className="col-md-8">
          <h5 className="mb-0">チームメンバー一覧</h5>
        </div>
        <div className="col-md-4 d-flex justify-content-end">
          {isAdmin && (
            <Link
              to={`/w/${_w_!.identifier}/setting/teams/${_t_!.id}/team_members/new`}
              className="btn bg-gradient-primary btn-icon"
            >
              <i className="fas fa-plus"></i>メンバー追加
            </Link>
          )}
        </div>
      </div>

      <ModalConfirm
        modalId={'deleteConfirm'}
        okClicked={deleteConfirmOk}
        message={'チームメンバーを削除します。よろしいですか？'}
      />

      <div className="card-body pt-0">
        <ApiErrorMessages error={error} />

        <div className="table-responsive">
          <table className="table align-items-center">
            <thead>
              <tr>
                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">ユーザ名</th>
                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">権限</th>
                <th className="text-secondary opacity-7" style={{ width: 100 }}></th>
                <th className="text-secondary opacity-7" style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {_t_!.team_members.map((m) => (
                <tr key={`member-${m.id}`}>
                  <td>
                    <div className="d-flex align-items-center">
                      <img
                        src={m.user.user_account.icon_file_url}
                        className="avatar avatar-sm me-2 rounded-circle"
                        alt="avatar image"
                      />
                      <h6 className="mb-0 text-sm">{m.user.user_account.name}</h6>
                    </div>
                  </td>
                  <td className="text-secondary text-wrap">
                    {m.roles.map((role) => (
                      <RoleBadge key={`role-${m.id}-${role.id}`} role={role} />
                    ))}
                  </td>
                  <td className="text-secondary text-wrap">
                    <DefaultApproverBadge team_member={m} />
                    <DefaultBackofficerBadge team_member={m} />
                  </td>
                  <td>
                    {isAdmin && (
                      <>
                        <Link
                          to={`/w/${_w_!.identifier}/setting/teams/${_t_!.id}/team_members/${m.id}/edit`}
                          className="btn btn-icon-only btn-outline-success"
                        >
                          <i className="fas fa-edit text-success text-sm"></i>
                        </Link>
                        <Link
                          to=""
                          data-bs-toggle="modal"
                          data-bs-target={`#deleteConfirm`}
                          onClick={() => setSelectedTeamMember(m)}
                          className="btn btn-icon-only btn-outline-danger ms-1"
                        >
                          <i className="fas fa-trash-alt text-danger text-sm"></i>
                        </Link>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RoleBadge = ({ role }: { role: Role }) => {
  if (role.name == 'admin') return <span className="badge badge-sm badge-primary me-1">チーム管理者</span>;

  if (role.name == 'approver') return <span className="badge badge-sm badge-success me-1">承認者</span>;

  if (role.name == 'financial_officer') return <span className="badge badge-sm badge-dark me-1">経理担当</span>;

  if (role.name == 'backoffice') return <span className="badge badge-sm badge-info me-1">バックオフィス担当</span>;

  if (role.name == 'member') return <span className="badge badge-sm badge-secondary me-1">メンバー</span>;

  return <></>;
};

const DefaultBackofficerBadge = ({ team_member }: { team_member: TeamMember }) => {
  if (team_member.default_backofficer)
    return <span className="badge badge-sm badge-danger me-1">メインバックオフィス担当者</span>;

  return <></>;
};

const DefaultApproverBadge = ({ team_member }: { team_member: TeamMember }) => {
  if (team_member.default_approver) return <span className="badge badge-sm badge-danger me-1">メイン承認担当者</span>;

  return <></>;
};
